import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEvents, createEvent, updateEvent, deleteEvent, getRoles,
  getRecurringEvents, createRecurringEvent, deleteRecurringEvent, generateRecurringEvents,
  getEventTypes
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Calendar as CalendarIcon, MapPin, Trash2, Clock, Edit,
  RefreshCw, List, Filter, Play, Repeat
} from 'lucide-react';
import { format, parseISO, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function CalendarPage() {
  const { hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all');
  const [tab, setTab] = useState('calendar');
  const [showCreate, setShowCreate] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showRecurring, setShowRecurring] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', event_type: 'evento',
    start_date: new Date().toISOString(), end_date: null, location: '', target_roles: []
  });
  const [recurringForm, setRecurringForm] = useState({
    title: '', description: '', event_type: 'ensayo', day_of_week: 0,
    start_time: '18:00', end_time: '20:00', location: '', is_active: true, target_roles: []
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, rolesRes, typesRes, recurRes] = await Promise.all([
        getEvents(), getRoles(), getEventTypes(), getRecurringEvents()
      ]);
      setEvents(eventsRes.data);
      setRoles(rolesRes.data);
      setEventTypes(typesRes.data);
      setRecurringEvents(recurRes.data);
    } catch (err) { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const resetForm = () => setForm({
    title: '', description: '', event_type: 'evento',
    start_date: new Date().toISOString(), end_date: null, location: '', target_roles: []
  });

  const handleCreateEvent = async () => {
    if (!form.title) return toast.error('El título es requerido');
    try {
      await createEvent(form);
      toast.success('Evento creado');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error('Error al crear evento'); }
  };

  const handleUpdateEvent = async () => {
    if (!form.title) return toast.error('El título es requerido');
    try {
      await updateEvent(editingEvent.id, form);
      toast.success('Evento actualizado');
      setEditingEvent(null);
      resetForm();
      fetchData();
    } catch (err) { toast.error('Error al actualizar evento'); }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await deleteEvent(id);
      toast.success('Evento eliminado');
      fetchData();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title, description: event.description || '', event_type: event.event_type,
      start_date: event.start_date, end_date: event.end_date, location: event.location || '', target_roles: event.target_roles || []
    });
  };

  const handleCreateRecurring = async () => {
    if (!recurringForm.title) return toast.error('El título es requerido');
    try {
      await createRecurringEvent(recurringForm);
      toast.success('Evento recurrente creado');
      setShowRecurring(false);
      setRecurringForm({ title: '', description: '', event_type: 'ensayo', day_of_week: 0, start_time: '18:00', end_time: '20:00', location: '', is_active: true, target_roles: [] });
      fetchData();
    } catch (err) { toast.error('Error al crear evento recurrente'); }
  };

  const handleGenerateRecurring = async (id) => {
    try {
      const res = await generateRecurringEvents(id, 4);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error('Error al generar eventos'); }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla recurrente?')) return;
    try {
      await deleteRecurringEvent(id);
      toast.success('Plantilla eliminada');
      fetchData();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const getEventTypeStyle = (type) => {
    const styles = {
      evento: { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-l-sky-500', dot: 'bg-sky-500' },
      ensayo: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-l-emerald-500', dot: 'bg-emerald-500' },
      reunion: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-l-amber-500', dot: 'bg-amber-500' },
    };
    return styles[type] || styles.evento;
  };

  const filteredEvents = events.filter(e => filterType === 'all' || e.event_type === filterType);
  const selectedDateEvents = filteredEvents.filter(e => { try { return isSameDay(parseISO(e.start_date), selectedDate); } catch { return false; } });
  const upcomingEvents = filteredEvents.filter(e => { try { return parseISO(e.start_date) >= new Date(); } catch { return false; } }).slice(0, 15);
  const eventDates = filteredEvents.map(e => { try { return parseISO(e.start_date); } catch { return null; } }).filter(Boolean);

  const typeStats = {
    evento: events.filter(e => e.event_type === 'evento').length,
    ensayo: events.filter(e => e.event_type === 'ensayo').length,
    reunion: events.filter(e => e.event_type === 'reunion').length,
  };

  const canCreate = hasPermission('eventos', 'crear');
  const canEdit = hasPermission('eventos', 'editar');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="calendar-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Calendario</h1>
          <p className="text-stone-500 text-sm">Eventos, ensayos y reuniones — {events.length} total</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <>
              <Button variant="outline" onClick={() => setShowRecurring(true)} data-testid="create-recurring-btn">
                <Repeat className="w-4 h-4 mr-2" /> Recurrente
              </Button>
              <Button onClick={() => { setShowCreate(true); resetForm(); }} data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Evento
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Type Stats & Filter */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(typeStats).map(([type, count]) => {
          const s = getEventTypeStyle(type);
          return (
            <Card
              key={type}
              className={`cursor-pointer transition-all ${filterType === type ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setFilterType(filterType === type ? 'all' : type)}
              data-testid={`filter-${type}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${s.dot}`} />
                <div>
                  <p className="text-2xl font-bold text-stone-800">{count}</p>
                  <p className="text-xs text-stone-500 capitalize">{type}s</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="calendar-tabs">
          <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarIcon className="w-4 h-4 mr-1.5" /> Calendario</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list"><List className="w-4 h-4 mr-1.5" /> Próximos</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="w-4 h-4 mr-1.5" /> Recurrentes</TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <Calendar
                  mode="single" selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={es} className="rounded-md"
                  modifiers={{ hasEvent: eventDates }}
                  modifiersStyles={{ hasEvent: { backgroundColor: 'rgb(236, 253, 245)', color: 'rgb(6, 78, 59)', fontWeight: '600' } }}
                />
                <div className="mt-4 space-y-2 text-sm">
                  {Object.entries(typeStats).map(([type]) => {
                    const s = getEventTypeStyle(type);
                    return (
                      <div key={type} className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${s.dot}`} />
                        <span className="capitalize">{type}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedDateEvents.length > 0 ? selectedDateEvents.map(event => {
                    const s = getEventTypeStyle(event.event_type);
                    return (
                      <div key={event.id} className={`p-4 rounded-lg bg-stone-50 border-l-4 ${s.border}`} data-testid={`event-card-${event.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`${s.bg} ${s.text}`}>{event.event_type}</Badge>
                              <span className="text-sm text-stone-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {(() => { try { return format(parseISO(event.start_date), 'HH:mm'); } catch { return ''; } })()}
                              </span>
                            </div>
                            <h4 className="font-medium text-stone-900">{event.title}</h4>
                            {event.description && <p className="text-sm text-stone-600 mt-1">{event.description}</p>}
                            {event.location && (
                              <p className="text-sm text-stone-500 mt-2 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{event.location}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {canEdit && (
                              <Button variant="ghost" size="icon" onClick={() => openEdit(event)} data-testid={`edit-event-${event.id}`}>
                                <Edit className="w-4 h-4 text-stone-500" />
                              </Button>
                            )}
                            {canCreate && (
                              <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteEvent(event.id)} data-testid={`delete-event-${event.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-center py-12 text-stone-400">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No hay eventos para esta fecha</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Upcoming List */}
        <TabsContent value="list">
          <Card>
            <CardHeader><CardTitle className="text-lg">Próximos Eventos</CardTitle></CardHeader>
            <CardContent>
              {upcomingEvents.length === 0 ? (
                <p className="text-center py-8 text-stone-400">No hay eventos próximos</p>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map(event => {
                    const s = getEventTypeStyle(event.event_type);
                    return (
                      <div key={event.id} className="flex items-center gap-4 p-3 rounded-lg border hover:bg-stone-50" data-testid={`upcoming-${event.id}`}>
                        <div className={`w-2 h-10 rounded-full ${s.dot}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800">{event.title}</p>
                          <div className="flex items-center gap-3 text-xs text-stone-500 mt-0.5">
                            <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />
                              {(() => { try { return format(parseISO(event.start_date), "d MMM, HH:mm", { locale: es }); } catch { return ''; } })()}
                            </span>
                            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>}
                          </div>
                        </div>
                        <Badge className={`${s.bg} ${s.text} shrink-0`}>{event.event_type}</Badge>
                        <div className="flex gap-1 shrink-0">
                          {canEdit && (
                            <button onClick={() => openEdit(event)} className="text-stone-400 hover:text-stone-700">
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {canCreate && (
                            <button onClick={() => handleDeleteEvent(event.id)} className="text-stone-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recurring Events */}
        <TabsContent value="recurring">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Plantillas Recurrentes</CardTitle>
                {canCreate && (
                  <Button size="sm" onClick={() => setShowRecurring(true)} data-testid="add-recurring-btn">
                    <Plus className="w-4 h-4 mr-1" /> Nueva Plantilla
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {recurringEvents.length === 0 ? (
                <p className="text-center py-8 text-stone-400">No hay eventos recurrentes configurados</p>
              ) : (
                <div className="space-y-3">
                  {recurringEvents.map(re => {
                    const s = getEventTypeStyle(re.event_type);
                    return (
                      <div key={re.id} className="flex items-center gap-4 p-4 rounded-lg border" data-testid={`recurring-${re.id}`}>
                        <div className={`p-2 rounded-lg ${s.bg}`}><Repeat className={`w-5 h-5 ${s.text}`} /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-stone-800">{re.title}</p>
                            <Badge className={re.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}>
                              {re.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </div>
                          <p className="text-sm text-stone-500 mt-0.5">
                            {DAY_NAMES[re.day_of_week]} — {re.start_time} a {re.end_time || '?'}
                            {re.location && ` — ${re.location}`}
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {canCreate && (
                            <Button variant="outline" size="sm" onClick={() => handleGenerateRecurring(re.id)} data-testid={`generate-${re.id}`}>
                              <Play className="w-4 h-4 mr-1" /> Generar 4 sem
                            </Button>
                          )}
                          {canCreate && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteRecurring(re.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showCreate || !!editingEvent} onOpenChange={() => { setShowCreate(false); setEditingEvent(null); resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Nombre del evento" data-testid="event-title" />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Evento</Label>
              <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="evento">Evento</SelectItem>
                  <SelectItem value="ensayo">Ensayo</SelectItem>
                  <SelectItem value="reunion">Reunión</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={e => setForm({ ...form, start_date: new Date(e.target.value).toISOString() })} data-testid="event-date" />
              </div>
              <div className="space-y-2">
                <Label>Hora</Label>
                <Input type="time" onChange={e => {
                  const [h, m] = e.target.value.split(':');
                  const d = new Date(form.start_date);
                  d.setHours(parseInt(h), parseInt(m));
                  setForm({ ...form, start_date: d.toISOString() });
                }} data-testid="event-time" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ubicación</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Lugar del evento" data-testid="event-location" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="event-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingEvent(null); }}>Cancelar</Button>
            <Button onClick={editingEvent ? handleUpdateEvent : handleCreateEvent} data-testid="submit-event-btn">
              {editingEvent ? 'Actualizar' : 'Crear Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Recurring Dialog */}
      <Dialog open={showRecurring} onOpenChange={setShowRecurring}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Evento Recurrente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={recurringForm.title} onChange={e => setRecurringForm({ ...recurringForm, title: e.target.value })} placeholder="Ej: Ensayo semanal" data-testid="recurring-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={recurringForm.event_type} onValueChange={v => setRecurringForm({ ...recurringForm, event_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evento">Evento</SelectItem>
                    <SelectItem value="ensayo">Ensayo</SelectItem>
                    <SelectItem value="reunion">Reunión</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Día de la Semana</Label>
                <Select value={String(recurringForm.day_of_week)} onValueChange={v => setRecurringForm({ ...recurringForm, day_of_week: parseInt(v) })}>
                  <SelectTrigger data-testid="recurring-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((name, i) => <SelectItem key={i} value={String(i)}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora Inicio</Label>
                <Input type="time" value={recurringForm.start_time} onChange={e => setRecurringForm({ ...recurringForm, start_time: e.target.value })} data-testid="recurring-start" />
              </div>
              <div className="space-y-2">
                <Label>Hora Fin</Label>
                <Input type="time" value={recurringForm.end_time} onChange={e => setRecurringForm({ ...recurringForm, end_time: e.target.value })} data-testid="recurring-end" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ubicación</Label>
              <Input value={recurringForm.location} onChange={e => setRecurringForm({ ...recurringForm, location: e.target.value })} placeholder="Lugar" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={recurringForm.description} onChange={e => setRecurringForm({ ...recurringForm, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurring(false)}>Cancelar</Button>
            <Button onClick={handleCreateRecurring} data-testid="submit-recurring-btn">Crear Plantilla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
